import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { RestApplicationClient } from "../../modules/core/services/D3AdminServices";

type Theme = "light" | "dark";

interface ThemeContextProps {
  theme: Theme;
  toggleTheme: () => void;
  setThemeMode: (themeMode?: string | null) => void;
}

const ThemeContext = createContext<ThemeContextProps | undefined>(undefined);

const normalizeTheme = (themeMode?: string | null): Theme | null => {
  if (!themeMode) {
    return null;
  }

  const normalized = themeMode.toLowerCase();
  return normalized === "dark" || normalized === "light" ? normalized : null;
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const restApplication = useMemo(() => RestApplicationClient(), []);
  const [theme, setTheme] = useState<Theme>(() => {
    return normalizeTheme(localStorage.getItem("theme")) ?? "light";
  });

  useEffect(() => {
    localStorage.setItem("theme", theme);
    document.body.setAttribute("data-theme", theme);
  }, [theme]);

  const setThemeMode = useCallback((themeMode?: string | null) => {
    const normalizedTheme = normalizeTheme(themeMode);
    if (normalizedTheme) {
      setTheme(normalizedTheme);
    }
  }, []);

  const toggleTheme = useCallback(() => {
    const previousTheme = theme;
    const nextTheme: Theme = theme === "light" ? "dark" : "light";
    setTheme(nextTheme);

    restApplication
      .updateThemeMode({ themeMode: nextTheme })
      .then((response: { type?: string; response?: { themeMode?: string | null } }) => {
        if (response?.type !== "SuccessResponse") {
          setTheme(previousTheme);
          return;
        }
        setThemeMode(response?.response?.themeMode);
      })
      .catch((error: unknown) => {
        console.error("Failed to update theme mode:", error);
        setTheme(previousTheme);
      });
  }, [theme, restApplication, setThemeMode]);

  const contextValue = useMemo(
    () => ({ theme, toggleTheme, setThemeMode }),
    [theme, toggleTheme, setThemeMode]
  );
  return (
    <ThemeContext.Provider value={contextValue}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useTheme must be used within ThemeProvider");
  return context;
};
